import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Radio,
  Navigation,
  Gauge,
  MapPin,
  RefreshCw,
  Send,
  PlusCircle,
  X,
  Search,
  Battery,
  ShieldCheck,
  Building2,
  Users,
  Compass
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import { fetchRoute, type RouteResult } from '@/shared/utils/routing';

export interface AgentLocation {
  id: string;
  username: string;
  displayName: string;
  department: string;
  role: string;
  isDriver?: boolean;
  vehicleNumber?: string;
  vehicleType?: string;
  driverStatus?: string | null;
  status: 'online' | 'idle' | 'offline';
  latitude: number;
  longitude: number;
  accuracy?: number;
  speed?: number;
  battery?: number;
  address?: string;
  deviceModel?: string;
  lastPingAt?: string | Date;
  destination?: {
    company?: string;
    contactPerson?: string;
    phone?: string;
    latitude?: number;
    longitude?: number;
    purpose?: string;
    address?: string;
    documentNumber?: string;
    documentType?: string;
    distanceKm?: number;
    etaMinutes?: number;
  };
}

/** Same classification the map markers already use — kept in one place so the
 *  "Drivers" filter and the marker styling can never disagree on who counts. */
function isDriverAgent(agent: AgentLocation): boolean {
  return Boolean(
    agent.isDriver || agent.role === 'driver' || agent.department === 'dispatch' ||
    (agent.displayName || '').includes('🚚')
  );
}

export function LiveFleetRadarPage() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const polylinesRef = useRef<any[]>([]);
  const routePolylineRef = useRef<any[]>([]);

  const [agents, setAgents] = useState<AgentLocation[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  // Real road route for the selected agent only (client-requested:
  // "Google Maps jaisa route selection, ye location se xyz location jana
  // hai" — previously every agent's line to its destination was a straight
  // "as the crow flies" dash, not a road route). Fetched only for whoever is
  // selected, not all agents at once — routing every agent on every 10s poll
  // would hammer the free OSRM demo server for no benefit; a dispatcher
  // looks at one route at a time.
  const [routeInfo, setRouteInfo] = useState<RouteResult | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'online' | 'idle'>('all');
  // Section filter — client-reported: "employees and drivers all show
  // together, no way to tell who's where." 'drivers' groups every agent the
  // page already treats as a driver (see isTruckDriver below); the rest are
  // grouped by their actual department so a Sales/Production/Dispatch lead
  // can filter down to just their own field staff.
  const [filterType, setFilterType] = useState<string>('all');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [dispatchModalOpen, setDispatchModalOpen] = useState<boolean>(false);
  const [dispatchForm, setDispatchForm] = useState({
    userId: '',
    company: '',
    contactPerson: '',
    phone: '',
    latitude: 19.0760,
    longitude: 72.8777,
    purpose: 'On-field deal negotiation'
  });

  // 1. Fetch live telemetry from backend
  const fetchLiveLocations = useCallback(async (showToast = false) => {
    setIsRefreshing(true);
    try {
      const res = await api.get<{ ok: boolean; agents: AgentLocation[] }>('/api/presence/live-locations');
      if (res.data?.agents) {
        setAgents(res.data.agents);
        if (showToast) toast.success('Fleet GPS Telemetry Synced');
      }
    } catch (err: any) {
      console.warn('Live location fetch fallback:', err?.message);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveLocations();
    const interval = setInterval(() => fetchLiveLocations(false), 10000);
    return () => clearInterval(interval);
  }, [fetchLiveLocations]);

  // 2. Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const L = (window as any).L;
    if (!L) return;

    if (!mapInstanceRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [19.1136, 72.8697], // Mumbai Industrial Corridor
        zoom: 11,
        zoomControl: true,
        attributionControl: false
      });

      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Street Basemap',
      }).addTo(map);

      mapInstanceRef.current = map;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // 3. Update Markers & Route lines
  useEffect(() => {
    const L = (window as any).L;
    const map = mapInstanceRef.current;
    if (!L || !map || agents.length === 0) return;

    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    polylinesRef.current.forEach((p) => map.removeLayer(p));
    polylinesRef.current = [];

    const bounds = L.latLngBounds([]);

    agents.forEach((agent) => {
      if (!agent.latitude || !agent.longitude) return;

      const agentPos = [agent.latitude, agent.longitude];
      bounds.extend(agentPos);

      const isSelected = agent.id === selectedAgentId;
      const isOnline = agent.status === 'online';
      const isTruckDriver = isDriverAgent(agent);

      const agentIconHtml = `
        <div style="position: relative; display: flex; align-items: center; justify-content: center; width: 44px; height: 44px;">
          <div style="position: absolute; width: 44px; height: 44px; border-radius: 50%; background: ${isTruckDriver ? 'rgba(5, 150, 105, 0.35)' : (isOnline ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.25)')}; animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
          <div style="position: relative; width: 34px; height: 34px; border-radius: 50%; background: ${isTruckDriver ? '#059669' : (isOnline ? '#10b981' : '#4f46e5')}; border: 2.5px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: ${isTruckDriver ? '16px' : '12px'};">
            ${isTruckDriver ? '🚚' : (agent.displayName || agent.username || 'A').slice(0, 1).toUpperCase()}
          </div>
        </div>
      `;

      const agentIcon = L.divIcon({
        html: agentIconHtml,
        className: 'custom-gps-marker',
        iconSize: [44, 44],
        iconAnchor: [22, 22]
      });

      const marker = L.marker(agentPos, { icon: agentIcon }).addTo(map);
      marker.bindPopup(`
        <div style="font-family: sans-serif; padding: 4px; min-width: 210px;">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">
            <span style="font-size: 16px;">${isTruckDriver ? '🚚' : '👤'}</span>
            <span style="font-weight: 800; font-size: 13px; color: #0f172a;">${agent.displayName || agent.username}</span>
          </div>
          ${agent.vehicleNumber ? `<div style="font-size: 11px; font-weight: bold; color: #059669; font-family: monospace;">Vehicle: ${agent.vehicleNumber} ${agent.vehicleType ? '(' + agent.vehicleType + ')' : ''}</div>` : ''}
          <div style="font-size: 11px; color: #64748b; margin: 3px 0 6px;">${agent.address || 'Active Corridor'}</div>
          <div style="display: flex; gap: 10px; font-size: 11px; font-weight: 700; color: ${isTruckDriver ? '#059669' : '#4338ca'};">
            <span>⚡ ${agent.speed ?? 0} km/h</span>
            <span>🔋 ${agent.battery != null ? agent.battery + '%' : '—'}</span>
            <span>📡 ${agent.status.toUpperCase()}</span>
          </div>
        </div>
      `);

      marker.on('click', () => {
        setSelectedAgentId(agent.id);
      });

      markersRef.current.push(marker);

      if (agent.destination?.latitude && agent.destination?.longitude) {
        const destPos = [agent.destination.latitude, agent.destination.longitude];
        bounds.extend(destPos);

        const destIconHtml = `
          <div style="display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; background: ${isTruckDriver ? '#059669' : '#4f46e5'}; border: 2px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.2); color: white; font-size: 14px;">
            ${isTruckDriver ? '🏭' : '🏢'}
          </div>
        `;

        const destIcon = L.divIcon({
          html: destIconHtml,
          className: 'custom-dest-marker',
          iconSize: [32, 32],
          iconAnchor: [16, 16]
        });

        const destMarker = L.marker(destPos, { icon: destIcon }).addTo(map);
        destMarker.bindPopup(`
          <div style="font-family: sans-serif; padding: 4px; min-width: 210px;">
            <div style="font-weight: 800; font-size: 11px; color: ${isTruckDriver ? '#059669' : '#4338ca'}; text-transform: uppercase;">
              ${isTruckDriver ? '📍 Delivery Destination' : 'Assigned Client Stop'}
            </div>
            <div style="font-weight: 700; font-size: 13px; color: #0f172a;">${agent.destination.company}</div>
            ${agent.destination.address ? `<div style="font-size: 11px; color: #64748b;">${agent.destination.address}</div>` : ''}
            <div style="font-size: 11px; color: #64748b; margin-top: 2px;">${agent.destination.purpose || 'Product Handover'}</div>
            <div style="margin-top: 5px; font-size: 11px; font-weight: 700; color: #059669;">
              ETA: ~${agent.destination.etaMinutes || 15} mins (${agent.destination.distanceKm || 4} km)
            </div>
          </div>
        `);
        markersRef.current.push(destMarker);

        const polyline = L.polyline([agentPos, destPos], {
          color: isTruckDriver ? '#059669' : (isSelected ? '#4f46e5' : '#818cf8'),
          weight: isSelected ? 4.5 : 3,
          opacity: 0.85,
          dashArray: '6, 8'
        }).addTo(map);
        polylinesRef.current.push(polyline);
      }
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [agents, selectedAgentId]);

  // 3b. Real road route for the selected agent, drawn as a solid overlay on
  // top of the straight dashed line every agent already gets above. Only
  // one agent is routed at a time (see routeInfo's declaration comment).
  useEffect(() => {
    const L = (window as any).L;
    const map = mapInstanceRef.current;
    if (!L || !map) return;

    routePolylineRef.current?.forEach?.((p: any) => map.removeLayer(p));
    routePolylineRef.current = [];
    setRouteInfo(null);

    const agent = agents.find((a) => a.id === selectedAgentId);
    if (!agent?.latitude || !agent?.longitude || !agent.destination?.latitude || !agent.destination?.longitude) return;

    let cancelled = false;
    fetchRoute(
      { lat: agent.latitude, lng: agent.longitude },
      { lat: agent.destination.latitude, lng: agent.destination.longitude },
    ).then((route) => {
      if (cancelled) return;
      setRouteInfo(route);
      const line = L.polyline(route.path, {
        color: '#4f46e5',
        weight: 5,
        opacity: 0.9,
      }).addTo(map);
      routePolylineRef.current = [line];
    });

    return () => { cancelled = true; };
  }, [agents, selectedAgentId]);

  const handleDispatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchForm.userId || !dispatchForm.company) {
      toast.error('Please select an agent and company name.');
      return;
    }

    try {
      const res = await api.post('/api/presence/assign-destination', dispatchForm);
      if (res.data?.ok) {
        toast.success(`Destination dispatched to agent!`);
        setDispatchModalOpen(false);
        fetchLiveLocations(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to dispatch route.');
    }
  };

  // Section options derived from whoever is actually on the map right now —
  // 'drivers' is a cross-department bucket (see isDriverAgent), the rest are
  // the real department values present, so this never lists a section with
  // nobody in it or misses one that's seeded outside the hardcoded list.
  const nonDriverDepartments = Array.from(
    new Set(agents.filter((a) => !isDriverAgent(a)).map((a) => a.department).filter(Boolean))
  ).sort();
  const typeOptions = ['all', 'drivers', ...nonDriverDepartments];

  const filteredAgents = agents.filter((a) => {
    const matchesSearch = (a.displayName || a.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (a.department || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || a.status === filterStatus;
    const matchesType = filterType === 'all' ||
      (filterType === 'drivers' ? isDriverAgent(a) : !isDriverAgent(a) && a.department === filterType);
    return matchesSearch && matchesFilter && matchesType;
  });

  const activeAgent = agents.find((a) => a.id === selectedAgentId) || agents[0];
  const onlineCount = agents.filter((a) => a.status === 'online').length;
  const enRouteCount = agents.filter((a) => (a.speed ?? 0) > 0).length;
  // Was a hardcoded "89%" regardless of what the fleet actually reported —
  // averaged only over agents whose device is actually sending a battery
  // reading, not fabricated for the rest.
  const agentsWithBattery = agents.filter((a) => a.battery != null);
  const avgBattery = agentsWithBattery.length > 0
    ? Math.round(agentsWithBattery.reduce((sum, a) => sum + (a.battery || 0), 0) / agentsWithBattery.length)
    : null;

  return (
    <div className="mx-auto max-w-[1600px] space-y-6 pb-12">
      <PageHeader
        icon={Compass}
        eyebrow={`Field Sales · ${onlineCount} rep${onlineCount === 1 ? '' : 's'} active`}
        title="Live GPS Radar"
        description="Where the field team is right now, the client visits assigned to them and their route history."
        actions={<>
          <Button variant="outline" onClick={() => fetchLiveLocations(true)} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            onClick={() => {
              if (agents.length > 0) setDispatchForm((p) => ({ ...p, userId: agents[0].id }));
              setDispatchModalOpen(true);
            }}
          >
            <PlusCircle className="w-4 h-4" /> Assign Client Visit
          </Button>
        </>}
      />

      {/* Metric Counters Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Field Fleet</div>
          <div className="text-2xl font-black text-slate-900 mt-1 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> {agents.length}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Registered mobile units</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-success/15 shadow-sm">
          <div className="text-[11px] font-bold text-success-text uppercase tracking-wider">Active On Duty</div>
          <div className="text-2xl font-black text-success-text mt-1 flex items-center gap-2">
            <Radio className="w-5 h-5 text-success-text animate-pulse" /> {onlineCount}
          </div>
          <div className="text-[11px] text-success-text mt-0.5">Transmitting live GPS</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-warning/15 shadow-sm">
          <div className="text-[11px] font-bold text-warning-text uppercase tracking-wider">En-Route In Transit</div>
          <div className="text-2xl font-black text-warning-text mt-1 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-warning-text" /> {enRouteCount}
          </div>
          <div className="text-[11px] text-warning-text mt-0.5">Moving to client stop</div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-indigo-100 shadow-sm">
          <div className="text-[11px] font-bold text-indigo-600 uppercase tracking-wider">Avg Fleet Battery</div>
          <div className="text-2xl font-black text-indigo-700 mt-1 flex items-center gap-2">
            <Battery className="w-5 h-5 text-indigo-500" /> {avgBattery != null ? `${avgBattery}%` : '—'}
          </div>
          <div className="text-[11px] text-indigo-600 mt-0.5">
            {agentsWithBattery.length > 0 ? `${agentsWithBattery.length} device(s) reporting` : 'No battery data yet'}
          </div>
        </div>
      </div>

      {/* Main Radar Layout: Large Map (8 Cols) + Detailed Agent & Route Sidebar (4 Cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left 8 Cols: Map Canvas */}
        <div className="lg:col-span-8 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm relative overflow-hidden flex flex-col justify-between">
          <div className="h-[620px] w-full rounded-xl overflow-hidden shadow-inner relative bg-slate-100">
            <div ref={mapContainerRef} className="w-full h-full" style={{ minHeight: '620px' }} />

            {/* Map Floating Control Box */}
            <div className="absolute top-4 right-4 z-[400] bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-3 shadow-lg text-xs space-y-2 max-w-[200px]">
              <div className="font-extrabold text-slate-900 border-b border-slate-100 pb-1 flex items-center justify-between">
                <span>Fleet Telemetry</span>
                <ShieldCheck className="w-3.5 h-3.5 text-success-text" />
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full bg-success inline-block shrink-0" />
                <span>Live Moving Rep</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-600">
                <span className="w-2.5 h-2.5 rounded-md bg-indigo-600 inline-block shrink-0" />
                <span>Client Destination</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-600">
                <span className="w-3 border-t-2 border-dashed border-indigo-500 inline-block shrink-0" />
                <span>Active Route Line</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right 4 Cols: Fleet Directory & Active Inspector */}
        <div className="lg:col-span-4 space-y-4 flex flex-col">
          {/* Search & Filter Strip */}
          <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search agent by name or dept..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-slate-50 text-slate-800 focus:outline-indigo-500"
              />
            </div>

            <div className="flex gap-1.5">
              {(['all', 'online', 'idle'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setFilterStatus(st)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold capitalize transition ${
                    filterStatus === st
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>

            {/* Section filter — Drivers vs. each department's own field staff */}
            <div className="flex flex-wrap gap-1.5">
              {typeOptions.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-bold capitalize transition ${
                    filterType === t
                      ? 'bg-success-text text-on-primary shadow-sm'
                      : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Agents List */}
          <div className="p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm space-y-2.5 max-h-[300px] overflow-y-auto">
            <div className="text-xs font-bold text-slate-800 pb-1 border-b border-slate-100 flex items-center justify-between">
              <span>Field Agents ({filteredAgents.length})</span>
              <span className="text-[10px] text-slate-400">Click to focus map</span>
            </div>

            {filteredAgents.map((ag) => {
              const isSelected = (selectedAgentId || agents[0]?.id) === ag.id;
              return (
                <div
                  key={ag.id}
                  onClick={() => {
                    setSelectedAgentId(ag.id);
                    if (mapInstanceRef.current && ag.latitude && ag.longitude) {
                      mapInstanceRef.current.setView([ag.latitude, ag.longitude], 14);
                    }
                  }}
                  className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                    isSelected
                      ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                      : 'bg-slate-50/60 border-slate-200/80 hover:bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-sm">
                      {(ag.displayName || ag.username || 'A').slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                        {ag.displayName || ag.username}
                        <span className={`w-2 h-2 rounded-full ${ag.status === 'online' ? 'bg-success' : 'bg-slate-400'}`} />
                      </div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[140px]">{ag.address || 'Active Corridor'}</div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[11px] font-extrabold text-indigo-700 flex items-center gap-1 justify-end">
                      <Gauge className="w-3 h-3 text-indigo-500" /> {ag.speed || 0} km/h
                    </div>
                    <div className="text-[10px] text-slate-400">🔋 {ag.battery != null ? `${ag.battery}%` : '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detailed Active Route Card */}
          {activeAgent && (
            <div className="p-5 rounded-2xl bg-indigo-50/70 via-white border border-indigo-100 shadow-sm space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-indigo-100">
                <span className="font-extrabold text-xs text-indigo-900 flex items-center gap-1.5">
                  <Navigation className="w-4 h-4 text-indigo-600" /> Active Destination Stop
                </span>
                {activeAgent.destination ? (
                  <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-indigo-600 text-white font-bold shadow-sm">
                    ETA ~{activeAgent.destination.etaMinutes || 15} min
                  </span>
                ) : (
                  <span className="text-[10px] px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                    No Stop Assigned
                  </span>
                )}
              </div>

              {activeAgent.destination ? (
                <>
                  <div>
                    <div className="text-xs text-slate-500">Destination Client</div>
                    <div className="text-sm font-extrabold text-slate-900 flex items-center gap-1.5 mt-0.5">
                      <Building2 className="w-4 h-4 text-indigo-600" /> {activeAgent.destination.company}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
                    <div>
                      <span className="text-slate-500 block">Contact Person</span>
                      <span className="font-bold text-slate-800">{activeAgent.destination.contactPerson || 'Client Rep'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">Phone</span>
                      <span className="font-bold text-slate-800">{activeAgent.destination.phone || '—'}</span>
                    </div>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white border border-indigo-100 text-[11px] text-slate-700 flex items-center justify-between">
                    <span className="flex items-center gap-1 font-bold text-indigo-600">
                      <MapPin className="w-3.5 h-3.5" />
                      {routeInfo
                        ? `${routeInfo.isRoad ? 'Road route' : 'Distance'}: ${routeInfo.distanceKm} km · ~${routeInfo.durationMin} min`
                        : activeAgent.destination.distanceKm
                          ? `Distance: ${activeAgent.destination.distanceKm} km`
                          : 'Routing…'}
                    </span>
                    <span className="text-slate-500">{activeAgent.destination.purpose || 'Client Visit'}</span>
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-slate-500 italic py-2">
                  No active client stop scheduled. Click "+ Dispatch Destination Visit" above to dispatch a visit to this agent.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dispatch Route Modal */}
      {dispatchModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2 text-indigo-600 font-bold text-sm">
                <Send className="w-4 h-4" />
                <span>Dispatch Client Destination</span>
              </div>
              <button
                onClick={() => setDispatchModalOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleDispatchSubmit} className="mt-4 space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Select Field Representative</label>
                <select
                  value={dispatchForm.userId}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, userId: e.target.value })}
                  className="w-full text-xs rounded-xl border border-slate-300 p-2.5 bg-white text-slate-800 focus:outline-indigo-500"
                  required
                >
                  {agents.map((ag) => (
                    <option key={ag.id} value={ag.id}>
                      {ag.displayName || ag.username} ({ag.department || 'Sales'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Client / Company Name</label>
                <input
                  type="text"
                  placeholder="e.g. Bharat Petroleum Refinery Unit 4"
                  value={dispatchForm.company}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, company: e.target.value })}
                  className="w-full text-xs rounded-xl border border-slate-300 p-2.5 bg-white text-slate-800 focus:outline-indigo-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    placeholder="e.g. Mr. Anil Mehta"
                    value={dispatchForm.contactPerson}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, contactPerson: e.target.value })}
                    className="w-full text-xs rounded-xl border border-slate-300 p-2.5 bg-white text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Contact Phone</label>
                  <input
                    type="text"
                    placeholder="+91 98765 43210"
                    value={dispatchForm.phone}
                    onChange={(e) => setDispatchForm({ ...dispatchForm, phone: e.target.value })}
                    className="w-full text-xs rounded-xl border border-slate-300 p-2.5 bg-white text-slate-800"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Visit Purpose / Deliverables</label>
                <input
                  type="text"
                  placeholder="e.g. Base Oil Tender discussion & sampling"
                  value={dispatchForm.purpose}
                  onChange={(e) => setDispatchForm({ ...dispatchForm, purpose: e.target.value })}
                  className="w-full text-xs rounded-xl border border-slate-300 p-2.5 bg-white text-slate-800"
                />
              </div>

              <div className="pt-3 flex items-center justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setDispatchModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-100"
                >
                  Dispatch Destination
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
